import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Target, 
  BookOpen, 
  BarChart3, 
  Settings,
  Heart,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Sparkles,
  ChevronDown,
  FileEdit,
  MonitorPlay,
  GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import foxHead from '@/assets/fox-head.png';

// navigation items moved inside component for i18n

interface DashboardSidebarProps {
  className?: string;
}

export const DashboardSidebar = ({ className }: DashboardSidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const isLibraryPath = location.pathname === '/biblioteka' || location.pathname === '/edytor' || location.pathname === '/prompter';
  const isLibraryOpen = isLibraryPath;

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  };

  const navigationItems = [
    { title: t('nav.dashboard'), href: '/panel', icon: LayoutDashboard },
    { title: t('nav.ideaGenerator'), href: '/kreator-scenariusza', icon: Sparkles },
    // Library will be inserted here as collapsible
    { title: t('nav.favorites'), href: '/ulubione', icon: Heart },
    { title: t('nav.inspiration'), href: '/inspiracje', icon: Lightbulb },
    { title: t('nav.analytics'), href: '/analityka', icon: BarChart3 },
    { title: t('nav.competitors'), href: '/konkurencja', icon: Target },
    { title: 'Akademia', href: '/akademia', icon: GraduationCap },
    { title: t('nav.settings'), href: '/ustawienia', icon: Settings },
  ];

  const librarySubItems = [
    { title: t('library.tabs.all'), href: '/biblioteka?tab=all' },
    { title: t('library.tabs.generated'), href: '/biblioteka?tab=generated' },
    { title: t('library.tabs.best'), href: '/biblioteka?tab=best' },
    { title: t('library.tabs.used'), href: '/biblioteka?tab=used' },
    { title: t('library.tabs.trash'), href: '/biblioteka?tab=trash' },
  ];

  const toolsItems = [
    { title: 'Edytor', href: '/edytor', icon: FileEdit },
    { title: 'Prompter', href: '/prompter', icon: MonitorPlay },
  ];

  return (
    <div className={cn(
      "hidden md:flex h-screen sticky top-0 z-50 transition-all duration-300 border-r bg-gradient-to-b from-card to-card/50 backdrop-blur-sm flex-shrink-0 shadow-sm",
      isCollapsed ? "w-[72px]" : "w-56",
      className
    )}>
      {/* Sidebar Content */}
      <div className="flex flex-col h-full p-3 w-full">
        {/* Logo/Brand with Toggle */}
        <div className={cn(
          "flex items-center mb-6 transition-all duration-300",
          isCollapsed ? "flex-col gap-3" : "justify-between"
        )}>
          {!isCollapsed ? (
            <div className="flex items-center gap-2">
              <img src={foxHead} alt="Viraluj mascot" className="w-7 h-7 object-contain flex-shrink-0" />
              <h2 className="text-lg font-bold leading-none bg-gradient-to-r from-orange-500 to-orange-400 bg-clip-text text-transparent">
                Viraluj.pl
              </h2>
            </div>
          ) : (
            <img src={foxHead} alt="Viraluj mascot" className="w-10 h-10 object-contain" />
          )}
          
          {/* Collapse Toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 rounded-lg border border-border/40 bg-background/80 hover:bg-primary/10 hover:border-primary/40 shadow-sm transition-all duration-200"
            onClick={handleToggle}
            aria-label={isCollapsed ? 'Rozwiń panel' : 'Zwiń panel'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-2" aria-label="Menu główne">
          {/* Dashboard */}
          {navigationItems.slice(0, 2).map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center rounded-lg text-sm font-medium transition-all duration-200 group",
                  "hover:bg-primary/10 hover:shadow-sm hover:translate-x-0.5",
                  isActive 
                    ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md" 
                    : "text-muted-foreground hover:text-foreground",
                  isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5"
                )}
              >
                <item.icon className={cn(
                  "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                  !isCollapsed && "mr-3",
                  !isActive && "group-hover:scale-110"
                )} />
                {isCollapsed ? (
                  <span className="sr-only">{item.title}</span>
                ) : (
                  <span className="truncate">{item.title}</span>
                )}
              </NavLink>
            );
          })}

          {/* Library Dropdown */}
          <Collapsible open={isLibraryOpen}>
            <CollapsibleTrigger asChild>
              <button
                onClick={() => navigate('/biblioteka?tab=all')}
                aria-label={isCollapsed ? t('nav.library') : undefined}
                className={cn(
                  "w-full flex items-center rounded-lg text-sm font-medium transition-all duration-200 group",
                  "hover:bg-primary/10 hover:shadow-sm hover:translate-x-0.5",
                  location.pathname === '/biblioteka'
                    ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground",
                  isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5"
                )}
              >
                <BookOpen className={cn(
                  "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                  !isCollapsed && "mr-3",
                  location.pathname !== '/biblioteka' && "group-hover:scale-110"
                )} />
                {!isCollapsed && (
                  <>
                    <span className="truncate flex-1 text-left">{t('nav.library')}</span>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      isLibraryOpen && "rotate-180"
                    )} />
                  </>
                )}
              </button>
            </CollapsibleTrigger>

            {!isCollapsed && (
              <CollapsibleContent className="space-y-1 mt-1">
                {librarySubItems.map((subItem) => (
                  <NavLink
                    key={subItem.href}
                    to={subItem.href}
                    className={cn(
                      "flex items-center pl-11 pr-3 py-2 rounded-lg text-sm transition-all duration-200",
                      "hover:bg-primary/10 hover:translate-x-0.5",
                      location.pathname + location.search === subItem.href
                        ? "bg-primary/20 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <span className="truncate">{subItem.title}</span>
                  </NavLink>
                ))}
                
                {/* Divider */}
                <div className="mx-3 my-2 border-t border-border/50" />
                
                {/* Tools: Edytor & Prompter */}
                {toolsItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "flex items-center pl-9 pr-3 py-2 rounded-lg text-sm transition-all duration-200",
                      "hover:bg-primary/10 hover:translate-x-0.5",
                      location.pathname === item.href
                        ? "bg-primary/20 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    <span className="truncate">{item.title}</span>
                  </NavLink>
                ))}
              </CollapsibleContent>
            )}
          </Collapsible>

          {/* Rest of navigation items */}
          {navigationItems.slice(2).map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center rounded-lg text-sm font-medium transition-all duration-200 group",
                  "hover:bg-primary/10 hover:shadow-sm hover:translate-x-0.5",
                  isActive 
                    ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md" 
                    : "text-muted-foreground hover:text-foreground",
                  isCollapsed ? "justify-center px-2 py-3" : "px-3 py-2.5"
                )}
              >
                <item.icon className={cn(
                  "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                  !isCollapsed && "mr-3",
                  !isActive && "group-hover:scale-110"
                )} />
                {isCollapsed ? (
                  <span className="sr-only">{item.title}</span>
                ) : (
                  <span className="truncate">{item.title}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
};